import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { APP_INITIALIZER, NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';

import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { IPublicClientApplication, InteractionType } from '@azure/msal-browser';
import {
    MsalGuard,
    MsalInterceptor,
    MsalBroadcastService,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration,
    MsalRedirectComponent,
} from '@azure/msal-angular';
import { FailedComponent } from './failed/failed.component';
import { RouterModule } from '@angular/router';
import { UiService } from './shared/services/ui.service';
import { CagovFooterComponent } from './components/cagov/cagov-footer/cagov-footer.component';
import {
    AppConfigService,
    createMsalConfig,
    createMsalInterceptor,
} from './app.config';
import { CaGovContentPanelComponent } from './components/cagov/cagov-content-panel/content-panel.component';
import { CaGovNavMegamenuComponent } from './components/cagov/cagov-nav-megamenu/nav-megamenu.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LoggerService } from './shared/services/logger.service';
import { QuillModule } from 'ngx-quill';
import { DestroyRxComponent } from './components/destroy-rx/destroy-rx.component';
import { HeaderInterceptorService } from './shared/services/header-interceptor.service';
import { LoginComponent } from './modules/login/login.component';
import { MatCardModule } from '@angular/material/card';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { IsActivePipe } from './shared/pipes/is-active.pipe';
import { SortByPipe } from './shared/pipes/sort-by.pipe';
import { CachedHttpService } from './shared/services/cached-http.service';
import { DashboardModule } from './modules/dashboard/dashboard.module';
import { AuthService } from './shared/services/auth.service';
import { NgOptimizedImage } from '@angular/common';
import { AppSharedModule } from './app-shared.module';
import { SessionComponent } from './components/session/session.component';
import { UserSettingsModalComponent } from './components/user-settings-modal/user-settings-modal.component';
import { environment } from '../environments/environment';
import { TimeoutModalComponent } from './components/timeout-modal/timeout-modal.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';

// Remove this line to use Angular Universal
const isIE =
    window.navigator.userAgent.indexOf('MSIE ') > -1 ||
    window.navigator.userAgent.indexOf('Trident/') > -1;

/**
 * Initializes the lambda function for getting the app configs.
 * @param configService Service providing the app configs.
 */
export function initConfig(configService: AppConfigService) {
    return () => configService.getConfig();
}

/**
 * Factory function to configure MSAL using the app configs.
 * @constructor
 */
export function MSALInstanceFactory(): IPublicClientApplication {
    return createMsalConfig(environment);
}

/**
 * Factory function to configure the MSAL interceptor using the app configs.
 * @constructor
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
    return createMsalInterceptor(environment);
}

/**
 * Factory function to configure the MSAL guard.
 * @constructor
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
    return {
        interactionType: InteractionType.Redirect,
        authRequest: {
            scopes: ['user.read'],
        },
        loginFailedRoute: '/login-failed',
    };
}

@NgModule({
    declarations: [
        AppComponent,
        CaGovNavMegamenuComponent,
        CagovFooterComponent,
        CaGovContentPanelComponent,
        FailedComponent,
        DestroyRxComponent,
        LoginComponent,
        SessionComponent,
        UserSettingsModalComponent,
        TimeoutModalComponent,
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        RouterModule,
        FormsModule,
        ReactiveFormsModule,
        AppSharedModule,
        HttpClientModule,
        MsalModule,
        QuillModule.forRoot(),
        DashboardModule,
        MatCardModule,
        MatGridListModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgOptimizedImage,
        NgIdleKeepaliveModule.forRoot(),
    ],
    providers: [
        { provide: 'Window', useValue: window },
        AuthService,
        UiService,
        LoggerService,
        CachedHttpService,
        IsActivePipe,
        SortByPipe,
        AppConfigService,
        {
            provide: APP_INITIALIZER,
            useFactory: initConfig,
            deps: [AppConfigService],
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderInterceptorService,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
        },
        {
            provide: MSAL_INSTANCE,
            useFactory: MSALInstanceFactory,
        },
        {
            provide: MSAL_GUARD_CONFIG,
            useFactory: MSALGuardConfigFactory,
        },
        {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
        },
        MsalService,
        MsalGuard,
        MsalBroadcastService,
    ],
    exports: [],
    bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
